import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useAuth } from '../context/AuthContext'
import { useCatalog } from '../context/CatalogContext'
import { createKioskOrder, type KioskReceipt } from '../services/kiosk'
import { trackUrl } from '../services/kioskTrack'
import { QrCode } from '../components/QrCode'
import { DEFAULT_STATION, type OrderLine, type Product } from '../types'
import { categoryColor } from '../utils/categoryColors'
import { stockState } from '../utils/stock'
import { formatTRY } from '../utils/money'
import { CheckIcon, XIcon } from '../components/icons'
import './KioskPage.css'

/** Sepette bu kadar süre dokunulmazsa sepet boşalır, ekran baştan başlar. */
const IDLE_MS = 90_000
/** Sıra numarası ekranı bu kadar kalır (karekodu okutacak kadar). */
const DONE_MS = 30_000

/**
 * Kiosk (30 Eki 2026): müşteri tezgahtaki tabletten siparişini kendisi
 * verir. Sipariş kasaya "ödeme bekliyor" olarak düşer, sıra numarası alır;
 * kasada ödenince mutfağa gider (supabase/kiosk.sql). Tartılı ürünler,
 * tükenenler ve kasada gizli ürünler burada yok.
 */
export function KioskPage() {
  const { admin, tenantId, tenantName, hasFeature, isOwner } = useAuth()
  const { activeCategories, productsByCategory, categories } = useCatalog()
  const photos = hasFeature('gorsel')

  /** Ürün kimliği -> adet. */
  const [cart, setCart] = useState<Record<string, number>>({})
  const [cat, setCat] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<KioskReceipt | null>(null)
  const [cartOpen, setCartOpen] = useState(false)

  const sellable = useCallback(
    (p: Product) => p.active && !p.byWeight && !p.soldOut && stockState(p) !== 'out',
    [],
  )
  const groups = useMemo(
    () =>
      activeCategories
        .map((c) => ({ category: c, products: productsByCategory(c.id).filter(sellable) }))
        .filter((g) => g.products.length > 0),
    [activeCategories, productsByCategory, sellable],
  )
  const byId = useMemo(() => new Map(groups.flatMap((g) => g.products).map((p) => [p.id, p])), [groups])
  const shown = cat ? groups.filter((g) => g.category.id === cat) : groups

  const lines: OrderLine[] = useMemo(
    () =>
      Object.entries(cart)
        .map(([id, qty]): OrderLine | null => {
          const p = byId.get(id)
          if (!p || qty <= 0) return null
          const category = categories.find((c) => c.id === p.categoryId)
          return {
            productId: p.id,
            name: p.name,
            unitPriceKurus: p.priceKurus,
            qty,
            lineTotalKurus: p.priceKurus * qty,
            station: p.station || category?.station || DEFAULT_STATION,
            byName: 'Kiosk',
          }
        })
        .filter((l): l is OrderLine => l !== null),
    [cart, byId, categories],
  )
  const count = lines.reduce((n, l) => n + l.qty, 0)
  const total = lines.reduce((n, l) => n + l.lineTotalKurus, 0)

  const reset = useCallback(() => {
    setCart({})
    setCat(null)
    setDone(null)
    setError(null)
    setCartOpen(false)
  }, [])

  // Dokunulmayan sepet boşalır: bir sonraki müşteri öncekinin sepetini görmesin.
  const touched = useRef(Date.now())
  const touch = () => {
    touched.current = Date.now()
  }
  useEffect(() => {
    if (count === 0 || done) return
    const t = window.setInterval(() => {
      if (Date.now() - touched.current > IDLE_MS) reset()
    }, 5_000)
    return () => window.clearInterval(t)
  }, [count, done, reset])

  const change = (id: string, by: number) => {
    touch()
    setCart((c) => {
      const next = Math.max(0, Math.min(99, (c[id] ?? 0) + by))
      const out = { ...c }
      if (next === 0) delete out[id]
      else out[id] = next
      return out
    })
  }

  const send = async () => {
    if (!tenantId || !admin || lines.length === 0 || sending) return
    setSending(true)
    setError(null)
    try {
      const r = await createKioskOrder(tenantId, admin, lines)
      setDone(r)
      setCart({})
      setCartOpen(false)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSending(false)
    }
  }

  if (done) return <KioskDone receipt={done} onNew={reset} />

  const cartPanel = (
    <>
      <header className="kcart__head">
        <h2>Sepetiniz</h2>
        {count > 0 && <span className="kcart__count">{count} ürün</span>}
        {cartOpen && (
          <button type="button" className="kcart__x" aria-label="Kapat" onClick={() => setCartOpen(false)}>
            <XIcon />
          </button>
        )}
      </header>
      {lines.length === 0 ? (
        <p className="kcart__empty">Ürünlere dokunarak sepetinize ekleyin.</p>
      ) : (
        <ul className="kcart__lines">
          {lines.map((l) => (
            <li key={l.productId} className="kline">
              <div className="kline__name">
                <b>{l.name}</b>
                <span>{formatTRY(l.lineTotalKurus)}</span>
              </div>
              <div className="kstep">
                <button type="button" aria-label={`${l.name} azalt`} onClick={() => change(l.productId, -1)}>
                  −
                </button>
                <span>{l.qty}</span>
                <button type="button" aria-label={`${l.name} artır`} onClick={() => change(l.productId, 1)}>
                  +
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <footer className="kcart__foot">
        <div className="kcart__total">
          <span>Toplam</span>
          <b>{formatTRY(total)}</b>
        </div>
        {error && <p className="kcart__err">{error}</p>}
        <button type="button" className="kbtn kbtn--primary kbtn--block" disabled={lines.length === 0 || sending} onClick={() => void send()}>
          {sending ? 'Gönderiliyor…' : 'Siparişi ver'}
        </button>
        <p className="kcart__hint">Ödeme kasada alınır.</p>
      </footer>
    </>
  )

  return (
    <div className="kiosk" onPointerDown={touch}>
      <main className="kmenu">
        <header className="kmenu__head">
          <div>
            <p className="kmenu__tenant">{tenantName}</p>
            <h1>Ne yemek istersiniz?</h1>
          </div>
          {/* Patron önizlemesi: tablette değil, kendi ekranında bakarken. */}
          {isOwner && admin?.role === 'owner' && (
            <a className="kmenu__exit" href={`/${tenantId}/ayarlar?bolum=kiosk`}>
              Önizlemeden çık
            </a>
          )}
        </header>

        <nav className="kcats" aria-label="Kategoriler">
          <button type="button" className={`kcat ${cat === null ? 'is-on' : ''}`} onClick={() => setCat(null)}>
            Tümü
          </button>
          {groups.map(({ category }) => (
            <button
              key={category.id}
              type="button"
              className={`kcat ${cat === category.id ? 'is-on' : ''}`}
              style={{ '--cat': categoryColor(category, categories).hex } as CSSProperties}
              onClick={() => setCat(category.id)}
            >
              {category.name}
            </button>
          ))}
        </nav>

        <div className="kmenu__scroll">
          {groups.length === 0 && <p className="kmenu__empty">Şu an satışta ürün yok.</p>}
          {shown.map(({ category, products }) => (
            <section
              key={category.id}
              className="kgrp"
              style={{ '--cat': categoryColor(category, categories).hex } as CSSProperties}
            >
              <h3>{category.name}</h3>
              <div className="kgrid">
                {products.map((p) => {
                  const qty = cart[p.id] ?? 0
                  return (
                    <button key={p.id} type="button" className={`kprod ${qty ? 'is-in' : ''}`} onClick={() => change(p.id, 1)}>
                      {photos && p.imageUrl ? (
                        <img className="kprod__img" src={p.imageUrl} alt="" loading="lazy" />
                      ) : (
                        <span className="kprod__ph" aria-hidden="true">
                          {p.name.trim().charAt(0).toLocaleUpperCase('tr')}
                        </span>
                      )}
                      <span className="kprod__body">
                        <b>{p.name}</b>
                        {p.description && <small>{p.description}</small>}
                        <span className="kprod__price">{formatTRY(p.priceKurus)}</span>
                      </span>
                      {qty > 0 && <span className="kprod__qty">{qty}</span>}
                    </button>
                  )
                })}
              </div>
            </section>
          ))}
        </div>
      </main>

      <aside className="kcart" aria-label="Sepet">
        {cartPanel}
      </aside>

      {/* Dar ekranda sepet altta bir çubuk, dokununca açılır. */}
      <div className="kbar">
        <button type="button" className="kbtn kbtn--primary kbtn--block" disabled={count === 0} onClick={() => setCartOpen(true)}>
          {count === 0 ? 'Sepetiniz boş' : `Sepet: ${count} ürün, ${formatTRY(total)}`}
        </button>
      </div>
      {cartOpen && (
        <div className="ksheet" onClick={() => setCartOpen(false)}>
          <div className="ksheet__panel" onClick={(e) => e.stopPropagation()}>
            {cartPanel}
          </div>
        </div>
      )}
    </div>
  )
}

/** Sipariş verildi ekranı: sıra no, karekod ve kod (gösterim sayfası da kullanıyor). */
export function KioskDone({ receipt: done, onNew: reset }: { receipt: KioskReceipt; onNew: () => void }) {
  // Geri sayım (1 Kas 2026, kullanıcı): müşteri ekranın ne zaman kapanacağını görsün.
  const total = DONE_MS / 1000
  const [left, setLeft] = useState(total)
  useEffect(() => {
    const t = window.setInterval(() => setLeft((n) => Math.max(0, n - 1)), 1000)
    return () => window.clearInterval(t)
  }, [])
  useEffect(() => {
    if (left === 0) reset()
  }, [left, reset])

  return (
    <div className="kiosk kiosk--done">
      <div className="kdone">
        <span className="kdone__check" aria-hidden="true">
          <CheckIcon />
        </span>
        <p className="kdone__label">Sıra numaranız</p>
        <b className="kdone__no">{done.takeawayNo ?? '-'}</b>
        {/* Kağıt yerine karekod (31 Eki 2026): müşteri telefonuyla okutur,
            sırasını takip eder; kasada da aynı karekod ya da kod okunur. */}
        {done.trackToken && (
          <div className="kdone__take">
            <QrCode value={trackUrl(done.trackToken)} size={180} label="Sipariş takip karekodu" />
            <div className="kdone__takeText">
              <b>Telefonunuzla okutun</b>
              <span>Siparişinizi takip edin, hazır olunca görün.</span>
              {done.pickupCode && (
                <div className="kdone__code">
                  <span>Kodunuz</span>
                  <b>{done.pickupCode}</b>
                </div>
              )}
            </div>
          </div>
        )}
        <p className="kdone__text">Ödemeyi kasada yapın. Siparişiniz ödemeden sonra hazırlanır.</p>
        <button type="button" className="kbtn kbtn--primary" onClick={reset}>
          Yeni sipariş
        </button>
        <div className="kdone__count" role="timer" aria-live="off">
          <span>Sipariş ekranına yönlendiriliyorsunuz</span>
          <b>{left}</b>
          <i className="kdone__bar" aria-hidden="true">
            <i style={{ width: `${(left / total) * 100}%` }} />
          </i>
        </div>
      </div>
    </div>
  )
}
